import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import MessageModel from "@/commons/models/Message";
import { SenderType } from "@/commons/models/Resident";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * 意見 API 操作クラス
 */
export default class Opinions {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET Opinions
   */
  public async gets(
    userStore: LoginUser,
    queryParams: {
      startIndex: number;
      endIndex: number;
      order: string;
    }
  ): Promise<Array<OpinionGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Opinions, formatParams);
    const response = await this.commonService.executeGet<Array<OpinionGetModel>>(userStore, requestUrl, this.constructor.name, queryParams, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET OpinionsCount
   */
  public async getCount(userStore: LoginUser, queryParams?: { confirmed: boolean }): Promise<number> {
    // countの場合は末尾に「count」をつける
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Opinions, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * PATCH Opinions
   */
  public async patch(userStore: LoginUser, caseId: number, opinion: MessageModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      caseId: caseId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OpinionMessages, formatParams);

    // APIのモデルに合わせてここで送信内容を調整
    const requestBody: any = {
      functionType: opinion.functionType,
      functionContents: opinion.functionContents,
      lineId: opinion.lineId,
      messageType: opinion.messageType,
      receiver: opinion.receiver,
      residentId: opinion.residentId,
      residentName: opinion.residentName,
      residentKanaNama: opinion.residentKanaName ? opinion.residentKanaName : "",
      residentRoomName: opinion.residentRoomName,
      familyId: opinion.familyId,
      familyName: opinion.familyName,
      sender: opinion.sender,
      sendTime: opinion.sendTime,
      writtenBy: opinion.writtenBy,
      writerId: opinion.writerId,
      caseId: opinion.caseId
    };

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OpinionGetModel {
  /** ログの際に利用する (YYYYMMDDhhmmssSSS) */
  datetime: number | string;
  /** 施設を示すID */
  facilityId: number;
  /** メッセージの種別 */
  messageType: MessageType;
  /** どの機能が使用されたかを集計するために利用する */
  functionType: FunctionType;
  /** 機能ごとに異なる差分を示すJSON */
  functionContents: FunctionContents;
  /** LineのID */
  lineId: string;
  /** メールアドレス */
  mailaddress: string;
  /** 送信種別。LINE:0、MAIL:1 */
  senderType: SenderType;
  /** メッセージを受信した側のID */
  receiver: string;
  /** 利用者を示すID */
  residentId: number;
  /** 利用者名 */
  residentName: string;
  /** 利用者名（ふりがな） */
  residentKanaName: string;
  /** 利用者の部屋名 */
  residentRoomName: string;
  /** 家族を示すID */
  familyId: number;
  /** 家族名 */
  familyName: string;
  /** メッセージを送信した側のID */
  sender: string;
  /** メッセージの送信時刻(YYYYMMDDhhmmssSSS) */
  sendTime: number;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 編集者 */
  writtenBy: string;
  /** 編集者のuserId */
  writerId: string;
  /** opinionのケースID */
  caseId: number;
  /** 利用者グループID */
  groupId: number;
}

/** メッセージ種別 */
export enum MessageType {
  TEXT = "text",
  IMAGE = "image",
  BUTTONS = "buttons",
  FLEX = "flex",
  FUNCTION = "function",
  MULTI = "multi"
}

/** 機能種別 */
export enum FunctionType {
  MESSAGE_LOG = 0,
  TODAY_STATUS = 1,
  SEND_PICTURE = 2,
  EVENT = 3,
  RESERVATION = 4,
  FACILITY = 5,
  OPINION = 6,
  SEND_MESSAGE = 7,
  SEND_MESSAGE_ADMIn = 8,
  DELETE_RESERVATION = 9,
  CONFIRM_OPINION = 10,
  EDIT_FAMILY_USER = 11,
  SEND_MESSAGE_OTHER_USER = 18,
  OTHER = 99
}

/** 機能種別毎の内容 */
export type FunctionContents = any;
